import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, Clock, Minus, Plus } from 'lucide-react';
import { useAgendaStore } from '@/store/agendaStore';
import { useAuthStore } from '@/store/authStore';
import { useClientStore } from '@/store/clientStore';
import { usePracticeStore } from '@/store/practiceStore';
import { apiClient } from '@/utils/api';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';

interface ConflictError {
  message: string;
  suggestedSlot?: {
    startTime: string;
    endTime: string;
    displayText: string;
  };
}

interface CreateAgendaAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultDate?: Date;
  defaultStartTime?: Date;
  onSuccess?: () => void;
}

export default function CreateAgendaAppointmentDialog({
  open,
  onOpenChange,
  defaultDate,
  defaultStartTime,
  onSuccess,
}: CreateAgendaAppointmentDialogProps) {
  const { toast } = useToast();
  const { createAppointment, loading } = useAgendaStore();
  const { user: currentUser } = useAuthStore();
  const { clients, fetchClients } = useClientStore();
  const { practices, fetchPractices } = usePracticeStore();

  const [formData, setFormData] = useState({
    clientId: '',
    practiceId: '',
    userId: '',
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    location: '',
    notes: '',
    status: 'SCHEDULED' as 'SCHEDULED' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED',
  });

  const [users, setUsers] = useState<Array<{ id: string; firstName: string; lastName: string; email: string }>>([]);
  const [endTimeManuallyEdited, setEndTimeManuallyEdited] = useState(false);
  const [conflictError, setConflictError] = useState<ConflictError | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  /** Durata automatica fine-inizio quando l’utente cambia l’ora di inizio (15 min da griglia, 1 h da “Nuovo appuntamento”). */
  const autoEndDurationMinutesRef = useRef(60);

  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const bumpStartMinutes = (delta: number) => {
    if (!formData.startTime) return;
    const d = new Date(formData.startTime);
    if (Number.isNaN(d.getTime())) return;
    d.setMinutes(d.getMinutes() + delta);
    const startTime = formatDateTimeLocal(d);
    let endTime = formData.endTime;
    const endD = new Date(formData.endTime);
    if (!Number.isNaN(endD.getTime()) && endD.getTime() <= d.getTime()) {
      endTime = formatDateTimeLocal(new Date(d.getTime() + autoEndDurationMinutesRef.current * 60 * 1000));
    }
    setFormData((prev) => ({ ...prev, startTime, endTime }));
    setConflictError(null);
    setValidationError(null);
  };

  const bumpEndMinutes = (delta: number) => {
    if (!formData.endTime || !formData.startTime) return;
    const d = new Date(formData.endTime);
    const startD = new Date(formData.startTime);
    if (Number.isNaN(d.getTime()) || Number.isNaN(startD.getTime())) return;
    d.setMinutes(d.getMinutes() + delta);
    if (d.getTime() <= startD.getTime()) {
      d.setTime(startD.getTime() + 15 * 60 * 1000);
    }
    setFormData((prev) => ({ ...prev, endTime: formatDateTimeLocal(d) }));
    setEndTimeManuallyEdited(true);
    setConflictError(null);
    setValidationError(null);
  };

  useEffect(() => {
    if (open) {
      autoEndDurationMinutesRef.current = defaultStartTime ? 15 : 60;

      // Fetch users
      apiClient.get('/users/assignable')
        .then((response) => {
          const usersData = response.data?.data || [];
          setUsers(usersData);
          
          // Cerca "Pier Giorgio Gori" come assegnatario predefinito
          const pierGiorgio = usersData.find((u: any) => 
            u.firstName?.toLowerCase().includes('pier') && 
            u.lastName?.toLowerCase().includes('gori')
          );
          
          let startDate: Date;
          let endDate: Date;

          if (defaultStartTime) {
            startDate = new Date(defaultStartTime);
            endDate = new Date(startDate.getTime() + 15 * 60 * 1000);
          } else {
            startDate = defaultDate ? new Date(defaultDate) : new Date();
            const now = new Date();
            if (startDate.toDateString() === now.toDateString() && startDate < now) {
              startDate.setHours(now.getHours() + 1, 0, 0, 0);
            } else {
              startDate.setHours(9, 0, 0, 0);
            }
            endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
          }

          setFormData({
            clientId: '',
            practiceId: '',
            userId: pierGiorgio?.id || currentUser?.id || '',
            title: '',
            description: '',
            startTime: formatDateTimeLocal(startDate),
            endTime: formatDateTimeLocal(endDate),
            location: '',
            notes: '',
            status: 'SCHEDULED',
          });
        })
        .catch((error) => {
          console.error('Error fetching users:', error);
          if (currentUser) {
            setUsers([{
              id: currentUser.id,
              firstName: currentUser.firstName || '',
              lastName: currentUser.lastName || '',
              email: currentUser.email || '',
            }]);
          }
          
          let startDate: Date;
          let endDate: Date;

          if (defaultStartTime) {
            startDate = new Date(defaultStartTime);
            endDate = new Date(startDate.getTime() + 15 * 60 * 1000);
          } else {
            startDate = defaultDate ? new Date(defaultDate) : new Date();
            const now = new Date();
            if (startDate.toDateString() === now.toDateString() && startDate < now) {
              startDate.setHours(now.getHours() + 1, 0, 0, 0);
            } else {
              startDate.setHours(9, 0, 0, 0);
            }
            endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
          }

          setFormData({
            clientId: '',
            practiceId: '',
            userId: currentUser?.id || '',
            title: '',
            description: '',
            startTime: formatDateTimeLocal(startDate),
            endTime: formatDateTimeLocal(endDate),
            location: '',
            notes: '',
            status: 'SCHEDULED',
          });
        });

      // Fetch clients - carica tutti i clienti (limit alto per evitare paginazione)
      fetchClients({ page: 1, limit: 1000 });
      
      // Fetch practices - carica tutte le pratiche
      fetchPractices({ page: 1, limit: 1000 });
      
      setEndTimeManuallyEdited(false);
      setConflictError(null);
      setValidationError(null);
    }
  }, [open, currentUser, defaultDate, defaultStartTime, fetchClients, fetchPractices]);

  // Auto-calcola endTime quando cambia startTime (stessa durata scelta all’apertura: 15 min o 1 h)
  useEffect(() => {
    if (formData.startTime && !endTimeManuallyEdited) {
      const startDate = new Date(formData.startTime);
      const mins = autoEndDurationMinutesRef.current;
      const endDate = new Date(startDate.getTime() + mins * 60 * 1000);
      setFormData((prev) => ({ ...prev, endTime: formatDateTimeLocal(endDate) }));
    }
  }, [formData.startTime, endTimeManuallyEdited]);

  // Filtra le pratiche per cliente selezionato
  const filteredPractices = formData.clientId
    ? practices.filter(p => p.clientId === formData.clientId)
    : practices;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setConflictError(null);
    setValidationError(null);

    if (!formData.title || !formData.startTime || !formData.endTime) {
      toast({
        title: 'Errore',
        description: 'Titolo, data/ora di inizio e fine sono obbligatori',
        variant: 'destructive',
      });
      return;
    }

    try {
      await createAppointment({
        title: formData.title,
        description: formData.description || undefined,
        clientId: formData.clientId || undefined,
        practiceId: formData.practiceId || undefined,
        userId: formData.userId || undefined,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: new Date(formData.endTime).toISOString(),
        location: formData.location || undefined,
        notes: formData.notes || undefined,
        status: formData.status,
      });

      toast({
        title: 'Appuntamento creato',
        description: 'L\'appuntamento è stato creato con successo.',
      });
      
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      const errorData = error.response?.data;
      const statusCode = error.response?.status;
      
      if (statusCode === 409 && errorData?.data?.suggestedSlot) {
        setConflictError({
          message: errorData.message,
          suggestedSlot: errorData.data.suggestedSlot,
        });
      } else {
        setValidationError(errorData?.message || 'Errore nella creazione dell\'appuntamento');
      }
    }
  };

  // Funzione per applicare l'orario suggerito
  const applySuggestedSlot = () => {
    if (conflictError?.suggestedSlot) {
      const suggestedStart = new Date(conflictError.suggestedSlot.startTime);
      const suggestedEnd = new Date(conflictError.suggestedSlot.endTime);
      setFormData(prev => ({
        ...prev,
        startTime: formatDateTimeLocal(suggestedStart),
        endTime: formatDateTimeLocal(suggestedEnd),
      }));
      setConflictError(null);
      setEndTimeManuallyEdited(true); // Mantieni l'ora di fine
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuovo Appuntamento</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Titolo */}
          <div>
            <Label htmlFor="title">Titolo *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Es: Sopralluogo, Riunione, Consegna documenti..."
              required
            />
          </div>

          {/* Descrizione */}
          <div>
            <Label htmlFor="description">Descrizione</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descrizione dell'appuntamento..."
              rows={2}
            />
          </div>

          {/* Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startTime">Data/Ora Inizio *</Label>
              <div className="flex gap-1 items-center mt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="Meno 15 minuti inizio"
                  onClick={() => bumpStartMinutes(-15)}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  id="startTime"
                  type="datetime-local"
                  step={900}
                  className="min-w-0 flex-1"
                  value={formData.startTime}
                  onChange={(e) => {
                    setFormData({ ...formData, startTime: e.target.value });
                    setConflictError(null);
                    setValidationError(null);
                  }}
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="Aumenta minuti inizio"
                  onClick={() => bumpStartMinutes(15)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="endTime">Data/Ora Fine *</Label>
              <div className="flex gap-1 items-center mt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="Meno 15 minuti fine"
                  onClick={() => bumpEndMinutes(-15)}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <Input
                  id="endTime"
                  type="datetime-local"
                  step={900}
                  className="min-w-0 flex-1"
                  value={formData.endTime}
                  onChange={(e) => {
                    setFormData({ ...formData, endTime: e.target.value });
                    setEndTimeManuallyEdited(true);
                    setConflictError(null);
                    setValidationError(null);
                  }}
                  min={formData.startTime}
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="Aumenta minuti fine"
                  onClick={() => bumpEndMinutes(15)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Alert per conflitto orario */}
          {conflictError && (
            <Alert variant="destructive" className="border-orange-500 bg-orange-50">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <AlertTitle className="text-orange-800">Orario non disponibile</AlertTitle>
              <AlertDescription className="text-orange-700">
                <p className="mb-2">{conflictError.message}</p>
                {conflictError.suggestedSlot && (
                  <div className="flex items-center gap-2 mt-3">
                    <Clock className="h-4 w-4" />
                    <span className="font-medium">Prossimo orario libero: {conflictError.suggestedSlot.displayText}</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="ml-2 border-orange-500 text-orange-700 hover:bg-orange-100"
                      onClick={applySuggestedSlot}
                    >
                      Usa questo orario
                    </Button>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Alert per errore di validazione */}
          {validationError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}

          {/* Cliente e Pratica */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="clientId">Cliente (opzionale)</Label>
              <Combobox
                options={clients.map(client => ({
                  value: client.id,
                  label: `${client.firstName} ${client.lastName}`,
                  description: client.phone || client.email || undefined,
                }))}
                value={formData.clientId}
                onValueChange={(value) => setFormData({ ...formData, clientId: value, practiceId: '' })}
                placeholder="Seleziona cliente..."
                searchPlaceholder="Cerca cliente..."
                emptyMessage="Nessun cliente trovato"
              />
            </div>

            <div>
              <Label htmlFor="practiceId">Pratica (opzionale)</Label>
              <Combobox
                options={filteredPractices.map(practice => ({
                  value: practice.id,
                  label: practice.title,
                  description: practice.practiceNumber || undefined,
                }))}
                value={formData.practiceId}
                onValueChange={(value) => setFormData({ ...formData, practiceId: value })}
                placeholder="Seleziona pratica..."
                searchPlaceholder="Cerca pratica..."
                emptyMessage={formData.clientId ? "Nessuna pratica per questo cliente" : "Seleziona prima un cliente"}
                disabled={!formData.clientId}
              />
            </div>
          </div>

          {/* Luogo */}
          <div>
            <Label htmlFor="location">Luogo</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Es: Ufficio, Cantiere, Indirizzo..."
            />
          </div>

          {/* Note */}
          <div>
            <Label htmlFor="notes">Note</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Note aggiuntive..."
              rows={2}
            />
          </div>

          {/* Stato e Assegnatario */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status">Stato</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value as any })}
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SCHEDULED">Programmato</SelectItem>
                  <SelectItem value="CONFIRMED">Confermato</SelectItem>
                  <SelectItem value="COMPLETED">Completato</SelectItem>
                  <SelectItem value="CANCELLED">Annullato</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="userId">Assegnatario</Label>
              <Combobox
                options={users.map(user => ({
                  value: user.id,
                  label: `${user.firstName} ${user.lastName}`,
                  description: user.email || undefined,
                }))}
                value={formData.userId}
                onValueChange={(value) => {
                  setFormData({ ...formData, userId: value });
                  setConflictError(null); // Reset errore quando cambia l'assegnatario
                }}
                placeholder="Seleziona assegnatario..."
                searchPlaceholder="Cerca utente..."
                emptyMessage="Nessun utente trovato"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annulla
            </Button>
            <Button type="submit" disabled={loading || !formData.title || !formData.startTime || !formData.endTime}>
              {loading ? 'Creazione...' : 'Crea Appuntamento'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
